import {
  Box,
  CircularProgress,
  Fade,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Tooltip,
  Typography,
} from '@mui/material';
import { Delete, Edit, SearchOff } from '@mui/icons-material';
import { Chip } from '@mui/material';
import { useRef, useState } from 'react';
import { useTheme, alpha } from '@mui/material/styles';
import type { ColumnDef } from '../../pages/projects/projectsColumns';
import type { ScheduleBadge } from '../../types/workOrdersTab';
import { useColumnWidths } from '../../hooks/useColumnWidths';
import { ColumnResizeHandle } from './ColumnResizeHandle';
import { layeredBg, useViewportFitMaxHeight } from './gridFreeze';

interface DataTableProps {
  columns: ColumnDef[];
  data: Record<string, unknown>[];
  loading: boolean;
  onRowClick?: (row: Record<string, unknown>) => void;
  onEditClick?: (row: Record<string, unknown>) => void;
  onDeleteClick?: (row: Record<string, unknown>) => void;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
  onSort?: (field: string) => void;
  /**
   * Opt-in Excel-style manual column resizing (BUG-2026-0183). When set, a
   * drag handle appears on each header cell and widths persist to localStorage
   * under `storageKey`. Leaving this off keeps the table's original auto layout
   * unchanged (all other DataTable callers). Requires `storageKey`.
   */
  resizable?: boolean;
  /** localStorage grid id for persisted widths (required when `resizable`). */
  storageKey?: string;
  /**
   * Opt-in viewport fit (BUG-2026-0206). Caps the table body to the space left
   * in the window below it and makes the container its own vertical scroller,
   * so the horizontal scrollbar sits at the bottom of the VISIBLE window
   * instead of under the last row, and the header row stays pinned while the
   * rows scroll. Default off — the other callers keep their page-flow layout.
   */
  stickyHeader?: boolean;
  /**
   * Extra pixels to leave free below the table when `stickyHeader` is on —
   * e.g. the page's Pagination bar height, so the pager stays visible too.
   */
  bottomReserve?: number;
}

const DATE_FIELDS = ['StartDate', 'EndDate', 'ExpectedStartDate', 'ExpectedEndDate', 'sysLastModifiedDate', 'sysCreateDate'];

const STATUS_DOT_COLORS: Record<string, string> = {
  Active: 'statusActive',
  Planning: 'statusPlanning',
  'On Hold': 'statusOnHold',
  Completed: 'statusCompleted',
};

const MONTH_NAMES = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const formatDate = (value: unknown): string => {
  if (value == null) return '';
  const d = new Date(String(value));
  if (isNaN(d.getTime())) return String(value);
  return `${MONTH_NAMES[d.getMonth()]} ${d.getDate()}, ${d.getFullYear()}`;
};

const formatCell = (field: string, value: unknown): string => {
  if (value == null) return '';
  if (DATE_FIELDS.includes(field)) return formatDate(value);
  return String(value);
};

const StatusCell = ({ value }: { value: unknown }) => {
  const theme = useTheme();
  const text = String(value ?? '');
  const colorKey = STATUS_DOT_COLORS[text];
  const dotColor = colorKey
    ? (theme.customColors as Record<string, string>)[colorKey]
    : theme.customColors.statusCompleted;

  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
      <Box
        sx={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          backgroundColor: dotColor,
          flexShrink: 0,
          boxShadow: `0 0 0 2.5px ${alpha(dotColor, 0.2)}`,
        }}
      />
      <Typography
        variant="body2"
        sx={{ fontSize: '0.8125rem', fontWeight: 500, letterSpacing: '-0.005em' }}
      >
        {text}
      </Typography>
    </Box>
  );
};

/**
 * Schedule badge (work-orders list — Phase 2, qa/quote-wo-schedule-lifecycle-
 * 2026-07-11). Value is a ScheduleBadge built by buildScheduleBadge; tones map
 * to theme.schedule tokens (no hex literals here):
 *   committed — filled sage (live work), submitted — outlined sage (locked
 *   plan), draft — outlined tentative sage, none — plain muted em-dash.
 */
const ScheduleBadgeCell = ({ value }: { value: unknown }) => {
  const theme = useTheme();
  const badge = (value ?? null) as ScheduleBadge | null;
  if (!badge || badge.tone === 'none') {
    return (
      <Typography variant="body2" sx={{ fontSize: '0.8125rem', color: 'text.disabled' }}>
        {badge?.label ?? '—'}
      </Typography>
    );
  }
  const toneColor = badge.tone === 'draft' ? theme.schedule.tentative : theme.schedule.committed;
  const filled = badge.tone === 'committed';
  return (
    <Chip
      size="small"
      label={badge.label}
      sx={{
        height: 22,
        fontWeight: 600,
        fontSize: '0.72rem',
        ...(filled
          ? { color: theme.palette.primary.contrastText, bgcolor: toneColor }
          : {
              color: toneColor,
              bgcolor: alpha(toneColor, 0.1),
              border: `1px solid ${alpha(toneColor, 0.45)}`,
            }),
      }}
    />
  );
};

export const DataTable = ({
  columns,
  data,
  loading,
  onRowClick,
  onEditClick,
  onDeleteClick,
  sortBy,
  sortDir = 'asc',
  onSort,
  resizable = false,
  storageKey,
  stickyHeader = false,
  bottomReserve = 0,
}: DataTableProps) => {
  const theme = useTheme();
  // Always call the hook (rules of hooks); it's inert unless `resizable` reads
  // from it. Falls back to a stable id so it never throws when misconfigured.
  const { getWidth, setWidth } = useColumnWidths(storageKey ?? '__datatable__');
  const [preview, setPreview] = useState<{ field: string; width: number } | null>(null);
  const resize = resizable && !!storageKey;
  // Viewport fit: the loading/empty branches below return without the
  // TableContainer, so gate the hook on the table actually existing — its
  // effect re-arms when the container (re)mounts instead of holding a dead ref.
  const containerRef = useRef<HTMLDivElement | null>(null);
  const hasTable = !loading && data.length > 0;
  // Gutter = the page's own bottom padding (pb:3) + <main>'s (py:2), so the
  // table + pager fit the window with NO residual page scroll.
  const maxBodyHeight = useViewportFitMaxHeight(
    stickyHeader && hasTable,
    containerRef,
    parseInt(theme.spacing(5), 10) + bottomReserve,
  );
  // Sticky header cells float over scrolled rows, so they need an OPAQUE
  // background reproducing the header row's alpha tint (see gridFreeze.ts).
  const headerTint = alpha(theme.table.headerBg, 0.7);
  const stickyHeadSx = stickyHeader
    ? { position: 'sticky' as const, top: 0, zIndex: 3, ...layeredBg(theme, headerTint) }
    : {};

  // Effective width for a column during render: live drag value if this column
  // is being dragged, otherwise the committed (persisted or default) width.
  const effWidth = (col: ColumnDef): number =>
    preview && preview.field === col.field ? preview.width : getWidth(col);

  if (loading) {
    return (
      <Fade in timeout={300}>
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 8 }}>
          <CircularProgress size={28} thickness={4} />
        </Box>
      </Fade>
    );
  }

  if (data.length === 0) {
    return (
      <Fade in timeout={400}>
        <Paper
          elevation={0}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            py: 8,
            border: `1px solid ${alpha(theme.border.color, 0.6)}`,
            borderRadius: '12px',
          }}
        >
          <SearchOff sx={{ fontSize: 44, color: alpha(theme.palette.text.primary, 0.15), mb: 1.5 }} />
          <Typography color="text.secondary" variant="body1" sx={{ fontWeight: 500 }}>
            No data found
          </Typography>
          <Typography sx={{ color: alpha(theme.palette.text.primary, 0.35), fontSize: '0.8125rem', mt: 0.5 }}>
            Try adjusting your filters
          </Typography>
        </Paper>
      </Fade>
    );
  }

  return (
    <Fade in timeout={300}>
      <Paper
        elevation={0}
        sx={{
          border: `1px solid ${alpha(theme.border.color, 0.6)}`,
          borderRadius: '12px',
          overflow: 'hidden',
          boxShadow: `0 1px 4px ${alpha('#000', 0.03)}, 0 4px 12px ${alpha('#000', 0.02)}`,
          width: '100%',
        }}
      >
        <TableContainer
          ref={containerRef}
          sx={{
            overflowX: 'auto',
            ...(maxBodyHeight && { maxHeight: maxBodyHeight, overflowY: 'auto' }),
            /* Thin scrollbar when needed, invisible when not */
            '&::-webkit-scrollbar': { height: 6 },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: alpha(theme.border.color, 0.4),
              borderRadius: 3,
            },
            '&::-webkit-scrollbar-thumb:hover': {
              backgroundColor: alpha(theme.border.color, 0.7),
            },
            '&::-webkit-scrollbar-track': { backgroundColor: 'transparent' },
            scrollbarWidth: 'thin',
            scrollbarColor: `${alpha(theme.border.color, 0.4)} transparent`,
          }}
        >
          <Table
            size="small"
            stickyHeader={stickyHeader}
            sx={
              resize
                ? { tableLayout: 'fixed', width: 'max-content', minWidth: '100%' }
                : { tableLayout: 'auto', minWidth: 700 }
            }
          >
            {resize && (
              <colgroup>
                {columns.map((col) => (
                  <col key={col.field} style={{ width: effWidth(col) }} />
                ))}
                {(onEditClick || onDeleteClick) && (
                  <col style={{ width: onEditClick && onDeleteClick ? 70 : 40 }} />
                )}
              </colgroup>
            )}
            <TableHead>
              <TableRow
                sx={{
                  backgroundColor: headerTint,
                  backdropFilter: 'blur(4px)',
                  '& .MuiTableCell-head': {
                    fontWeight: 600,
                    fontSize: '0.75rem',
                    color: alpha(theme.palette.text.primary, 0.65),
                    textTransform: 'uppercase',
                    letterSpacing: '0.04em',
                    borderBottom: `1px solid ${alpha(theme.border.color, 0.5)}`,
                    py: 1.25,
                    whiteSpace: 'nowrap',
                  },
                }}
              >
                {columns.map((col) => (
                  <TableCell
                    key={col.field}
                    sx={{
                      ...(resize
                        ? {
                            // A sticky cell is already a positioned box for
                            // the absolute resize handle, so only fall back
                            // to `relative` when the header isn't sticky.
                            ...(!stickyHeader && { position: 'relative' }),
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                          }
                        : {
                            ...(col.width && { minWidth: col.width, width: col.width }),
                            ...(col.flex && { minWidth: col.flex * 80 }),
                          }),
                      ...stickyHeadSx,
                    }}
                  >
                    {col.sortable && onSort ? (
                      <TableSortLabel
                        active={sortBy === col.field}
                        direction={sortBy === col.field ? sortDir : 'asc'}
                        onClick={() => onSort(col.field)}
                        sx={{
                          '&.MuiTableSortLabel-root': {
                            color: 'inherit',
                            '&:hover': { color: theme.palette.text.primary },
                          },
                          '&.Mui-active': {
                            color: theme.palette.primary.main,
                            '& .MuiTableSortLabel-icon': {
                              color: theme.palette.primary.main,
                            },
                          },
                        }}
                      >
                        {col.headerName}
                      </TableSortLabel>
                    ) : (
                      col.headerName
                    )}
                    {resize && (
                      <ColumnResizeHandle
                        width={effWidth(col)}
                        onPreview={(w) => setPreview({ field: col.field, width: w })}
                        onCommit={(w) => {
                          setPreview(null);
                          setWidth(col.field, w);
                        }}
                      />
                    )}
                  </TableCell>
                ))}
                {(onEditClick || onDeleteClick) && (
                  <TableCell sx={{ width: onEditClick && onDeleteClick ? 70 : 40, ...stickyHeadSx }} />
                )}
              </TableRow>
            </TableHead>
            <TableBody>
              {data.map((row, idx) => (
                <TableRow
                  key={idx}
                  onClick={() => onRowClick?.(row)}
                  sx={{
                    cursor: onRowClick ? 'pointer' : 'default',
                    backgroundColor: idx % 2 === 1
                      ? alpha(theme.table.stripeBg, 0.4)
                      : 'background.paper',
                    transition: 'background-color 0.2s ease, box-shadow 0.2s ease',
                    '&:hover': {
                      backgroundColor: `${alpha(theme.palette.primary.main, 0.08)} !important`,
                      '& .MuiTableCell-body': {
                        color: theme.palette.text.primary,
                      },
                    },
                    height: theme.table.rowHeight,
                    '& .MuiTableCell-body': {
                      fontSize: '0.8125rem',
                      py: 0.875,
                      borderBottom: `1px solid ${alpha(theme.border.color, 0.3)}`,
                      color: alpha(theme.palette.text.primary, 0.85),
                      letterSpacing: '-0.005em',
                      transition: 'color 0.15s ease',
                    },
                  }}
                >
                  {columns.map((col) => {
                    const cellText = col.type ? '' : formatCell(col.field, row[col.field]);
                    return (
                      <TableCell
                        key={col.field}
                        sx={{
                          // In resize mode the <colgroup> governs width; a fixed
                          // maxWidth would fight it, so omit it there.
                          ...(resize ? {} : { maxWidth: col.width ?? 200 }),
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {col.type === 'status' ? (
                          <StatusCell value={row[col.field]} />
                        ) : col.type === 'scheduleBadge' ? (
                          <ScheduleBadgeCell value={row[col.field]} />
                        ) : (
                          <Tooltip title={cellText} enterDelay={500} disableHoverListener={cellText.length < 25}>
                            <span>{cellText}</span>
                          </Tooltip>
                        )}
                      </TableCell>
                    );
                  })}
                  {(onEditClick || onDeleteClick) && (
                    <TableCell sx={{ px: 0.5 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                        {onEditClick && (
                          <Tooltip title="Edit">
                            <Edit
                              onClick={(e) => { e.stopPropagation(); onEditClick(row); }}
                              sx={{
                                fontSize: 16,
                                color: alpha(theme.palette.text.primary, 0.3),
                                cursor: 'pointer',
                                transition: 'color 0.15s',
                                '&:hover': { color: theme.palette.primary.main },
                              }}
                            />
                          </Tooltip>
                        )}
                        {onDeleteClick && (
                          <Tooltip title="Delete">
                            <Delete
                              onClick={(e) => { e.stopPropagation(); onDeleteClick(row); }}
                              sx={{
                                fontSize: 16,
                                color: alpha(theme.palette.text.primary, 0.2),
                                cursor: 'pointer',
                                transition: 'color 0.15s',
                                '&:hover': { color: theme.palette.error.main },
                              }}
                            />
                          </Tooltip>
                        )}
                      </Box>
                    </TableCell>
                  )}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Fade>
  );
};
